import { z } from "zod";
import { VSM_PLAN_STATUSES } from "@playbookai/types";
import { idString } from "./common.js";
export const KAIZEN_OWNER_FILTERS = [
    "me",
    "any",
    "unassigned",
    "selected",
];
/**
 * `@2` due filters. `no_target_date` is gone: a due date is a precondition of
 * promotion, so the only Kaizen without one is an unpromoted opportunity — which
 * the stage axis below already names, and names better.
 */
export const KAIZEN_DUE_FILTERS = [
    "any",
    "overdue",
    "next_7_days",
    "next_30_days",
];
/**
 * Whether a Kaizen has become a commitment. Read from the system-owned
 * `inImplementationPlan` flag, exactly like the register's Stage column, so the
 * filter and the column can never disagree about the same row.
 */
export const KAIZEN_STAGE_FILTERS = [
    "any",
    "promoted",
    "not-promoted",
];
/**
 * TWO axes, not one, because the register shows two columns.
 *
 * The retired single `statuses` axis filtered on a Kaizen's own status, which no
 * longer exists. Replacing it with one merged list — promotion stage and plan
 * status in the same enum — was the obvious move and is wrong for the same reason
 * the Stage column exists: Status would carry two vocabularies, and "show me all
 * promoted work" becomes inexpressible, because selecting every plan status still
 * misses a promoted Kaizen whose plan row was deleted by hand.
 *
 * Split, each axis mirrors a column the reader can actually see:
 *   `stage`        → the Stage column, from the promotion flag
 *   `planStatuses` → the Status column, from the Implementation Plan row
 */
const commonFilters = {
    owner: z.enum(KAIZEN_OWNER_FILTERS),
    /** Account owners; additive so existing connections retain their meaning. */
    ownerIds: z.array(idString).max(24).optional(),
    stage: z.enum(KAIZEN_STAGE_FILTERS),
    planStatuses: z
        .array(z.enum(VSM_PLAN_STATUSES))
        .min(1)
        .max(VSM_PLAN_STATUSES.length),
    dueState: z.enum(KAIZEN_DUE_FILTERS),
};
/**
 * How many Kaizens a register ships: the newest few, most recently updated
 * first, on Personal Home (across every value stream the reader belongs to) and
 * on a value stream's monitoring dashboard alike. A register is a glance at
 * what moved, not the whole pile — the map and the Implementation Plan hold
 * that — and the provider reads only these rows from the database, so the cap
 * is also what keeps a read constant in the size of the pile.
 *
 * The wire contract (`KaizenListOutputSchema`) still admits up to 50 rows, so a
 * shell and a backend deployed a few minutes apart still agree while a rollout
 * is in flight. This is what the provider sends; that is what a reader accepts.
 */
export const KAIZEN_LIST_ROW_LIMIT = 5;
export const KaizenTableParamsSchema = z.object({}).strict();
export const defaultKaizenTableParams = {};
export const KaizenHealthParamsSchema = z.object({}).strict();
export const defaultKaizenHealthParams = {};
export const PersonalKaizenListInputSchema = z
    .object({
    ...commonFilters,
    vsmIds: z.array(idString).max(24),
})
    .strict()
    .refine((input) => input.owner !== "selected" || (input.ownerIds?.length ?? 0) > 0, { message: "Choose at least one owner." });
export const VsmKaizenListInputSchema = z
    .object(commonFilters)
    .strict()
    .refine((input) => input.owner !== "selected" || (input.ownerIds?.length ?? 0) > 0, { message: "Choose at least one owner." });
/**
 * Every plan status selected. `.min(1)` forbids the empty array, so "not
 * filtering by status" has to be spelled as "all of them" — and the resolver
 * reads a full selection as NOT NARROWING, which is what keeps unpromoted
 * Kaizens visible under an untouched filter. See `kaizenPlanStatusFilterNarrows`.
 */
const ALL_PLAN_STATUSES = [...VSM_PLAN_STATUSES];
/**
 * Does this status selection actually exclude anything?
 *
 * A full selection does not, and that distinction carries real meaning: an
 * unpromoted Kaizen has NO plan status, so any narrowed status filter must hide
 * it — you asked for blocked work, and an idea is not blocked work. But the
 * default selection must not hide the entire idea pile, which is exactly what a
 * naive `planStatuses.includes(row.planStatus)` would do to every unpromoted row.
 *
 * Shared so the resolver and its specs cannot drift on the rule.
 */
export function kaizenPlanStatusFilterNarrows(planStatuses) {
    // DISTINCT values, not element count. `.max()` caps the ARRAY, not the
    // selection, and nothing rejects duplicates — so four copies of "blocked"
    // would otherwise measure as "all four statuses selected" and switch the
    // status axis off entirely, returning every row for a filter that asked only
    // for blocked work.
    return new Set(planStatuses).size < VSM_PLAN_STATUSES.length;
}
export const DEFAULT_PERSONAL_KAIZEN_FILTERS = {
    owner: "me",
    stage: "any",
    planStatuses: ALL_PLAN_STATUSES,
    dueState: "any",
    vsmIds: [],
};
export const DEFAULT_VSM_KAIZEN_FILTERS = {
    owner: "any",
    stage: "any",
    planStatuses: ALL_PLAN_STATUSES,
    dueState: "any",
};
//# sourceMappingURL=dashboardKaizen.js.map